/* ============================================================
   useTableCardLabels — presentation only.

   On phones every `table.data` re-flows into one card per row (mobile.css).
   A card shows each value next to its column name, which CSS can only read
   from an attribute — so this copies each header's text onto its body cells
   as `data-label`, and tags the checkbox / identity / actions cells with
   `data-cell` so the card can lay them out.

   It never touches data, handlers or React state: it only sets attributes on
   cells that are already rendered, and re-runs (batched to one frame) when a
   table's rows change. Runs only while the phone breakpoint matches.
   ============================================================ */
import { useEffect } from 'react'

const PHONE = '(max-width: 640px)'

function headerText(th) {
  const copy = th.cloneNode(true)
  copy.querySelectorAll('.sort-ind').forEach((n) => n.remove())
  return copy.textContent.replace(/\s+/g, ' ').trim()
}

const isCheckCell = (cell) =>
  cell.querySelector('input[type=checkbox], .checkbox') && !cell.textContent.trim()

function setAttr(el, name, value) {
  if (value) {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value)
  } else if (el.hasAttribute(name)) {
    el.removeAttribute(name)
  }
}

function labelTable(table) {
  const head = table.tHead?.rows[0]
  if (!head) return
  const heads = [...head.cells]
  const labels = heads.map(headerText)
  heads.forEach((th) => setAttr(th, 'data-cell', isCheckCell(th) ? 'check' : null))

  for (const body of table.tBodies) {
    for (const row of body.rows) {
      const cells = [...row.cells]
      // Empty-state and group rows span the table; they stay full width.
      if (cells.length !== labels.length) continue
      let primary = false
      cells.forEach((td, i) => {
        let role = null
        if (isCheckCell(td)) role = 'check'
        else if (!primary) { role = 'primary'; primary = true }
        else if (!labels[i] && i === cells.length - 1) role = 'actions'
        setAttr(td, 'data-cell', role)
        setAttr(td, 'data-label', role === 'primary' || role === 'check' ? null : labels[i])
      })
    }
  }
}

export default function useTableCardLabels() {
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const mq = window.matchMedia(PHONE)
    let observer = null
    let frame = 0

    const run = () => {
      frame = 0
      document.querySelectorAll('table.data').forEach(labelTable)
    }
    const schedule = () => { if (!frame) frame = requestAnimationFrame(run) }

    const start = () => {
      if (observer) return
      run()
      observer = new MutationObserver(schedule)
      observer.observe(document.body, { childList: true, subtree: true })
    }
    const stop = () => {
      observer?.disconnect()
      observer = null
      if (frame) cancelAnimationFrame(frame)
      frame = 0
    }
    const sync = () => (mq.matches ? start() : stop())

    sync()
    mq.addEventListener('change', sync)
    return () => {
      mq.removeEventListener('change', sync)
      stop()
    }
  }, [])
}
